"use client";

import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  Cable,
  CheckCircle2,
  Copy,
  ExternalLink,
  Gauge,
  Layers3,
  MapPinned,
  Pencil,
  QrCode,
  Waves,
  Wrench,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useMemo, useState } from "react";

import { GuardedActionLink } from "@/components/auth";
import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import type { DeviceChannelDto, DeviceDto } from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";
import { PageHeader, PageSection } from "@/components/ds/workspace-page";
import { Card } from "@/components/ui-system";
import {
  deviceActivityModelFromApi,
  DeviceOperationalStatus,
  formatDeviceOperationalStatusLabel,
} from "@/lib/devices/device-state-model";
import {
  formatDeviceLastSeen,
  getDeviceLifecycleDates,
  getDeviceLocationLabel,
  getDeviceProtocolLabel,
  getDeviceSamplingLabel,
} from "@/lib/devices/device-field-helpers";

import { PrivateDeviceImage } from "@/modules/devices/components/private-device-image";
import {
  BluetoothWebSerialBridge,
  DataloggerConnectionStatus,
  useDataloggerConnections,
} from "@/modules/devices/components/DataloggerConnectionStatus";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

export type DeviceDetailViewProps = {
  device: DeviceDto;
};

function asJsonObject(value: unknown): JsonObject {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {};
}

function readMetaString(meta: JsonObject, key: string): string {
  const value = meta[key];
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function readMetaNumber(meta: JsonObject, key: string): number | null {
  const value = meta[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function normalizeTopicSegment(value: string): string {
  return value.trim().replace(/^\/+|\/+$/g, "");
}

function resolveDeviceMqttTopic(
  meta: JsonObject,
  hardwareMeta: JsonObject,
  deviceUid: string,
): string | null {
  const protocolMeta = asJsonObject(hardwareMeta.protocolSettings);
  const exactTopic =
    readMetaString(protocolMeta, "mqttTopic") || readMetaString(meta, "mqttTopic");
  if (exactTopic) {
    return normalizeTopicSegment(exactTopic.replaceAll("{device_uid}", deviceUid));
  }

  const topicPrefix =
    readMetaString(protocolMeta, "mqttTopicPrefix") ||
    readMetaString(meta, "mqttTopicPrefix");
  if (topicPrefix) {
    const normalizedPrefix = normalizeTopicSegment(
      topicPrefix.replaceAll("{device_uid}", deviceUid),
    );
    return `${normalizedPrefix}/${deviceUid}/telemetry`;
  }

  return `devices/${deviceUid}/telemetry`;
}

function buildSampleChannelValue(channel: DeviceChannelDto, index: number): number {
  const min = typeof channel.min_value === "number" ? channel.min_value : null;
  const max = typeof channel.max_value === "number" ? channel.max_value : null;

  if (min != null && max != null && Number.isFinite(min) && Number.isFinite(max) && min <= max) {
    return Number(((min + max) / 2).toFixed(2));
  }

  if (min != null && Number.isFinite(min)) {
    return Number((min + 1).toFixed(2));
  }

  if (max != null && Number.isFinite(max)) {
    return Number((max / 2).toFixed(2));
  }

  return Number((20 + index * 3.5).toFixed(2));
}

function getChannelMetadata(meta: JsonObject, deviceChannels: DeviceDto["channels"] | undefined) {
  const raw = meta.channels;
  if (!Array.isArray(raw)) return [];

  return raw.map((item, index) => {
    const channelMeta =
      item && typeof item === "object" && !Array.isArray(item) ? (item as JsonObject) : {};
    const apiChannel = deviceChannels?.[index];
    const thresholdLow = readMetaNumber(channelMeta, "thresholdWarningLow");
    const thresholdHigh = readMetaNumber(channelMeta, "thresholdWarningHigh");

    return {
      id:
        readMetaString(channelMeta, "id") ||
        apiChannel?.id ||
        `${readMetaString(channelMeta, "channelKey") || apiChannel?.key || "channel"}-${index}`,
      label:
        readMetaString(channelMeta, "name") ||
        apiChannel?.label ||
        apiChannel?.key ||
        `Channel ${index + 1}`,
      unit: apiChannel?.unit || readMetaString(channelMeta, "unit") || "",
      prefix: readMetaString(channelMeta, "prepend"),
      suffix: readMetaString(channelMeta, "append"),
      thresholdLow,
      thresholdHigh,
    };
  });
}

function StatusBadge({
  status,
}: {
  status: DeviceOperationalStatus;
}) {
  const cfg = {
    [DeviceOperationalStatus.ONLINE]: {
      label: "Online",
      className:
        "border-success/35 bg-success/10 text-success shadow-card",
      pulse: true,
    },
    [DeviceOperationalStatus.OFFLINE]: {
      label: "Offline",
      className: "border-border-strong/50 bg-muted-bg/80 text-muted",
      pulse: false,
    },
    [DeviceOperationalStatus.STALE]: {
      label: "Stale",
      className: "border-warning/40 bg-warning/10 text-warning",
      pulse: false,
    },
    [DeviceOperationalStatus.ERROR]: {
      label: "Error",
      className: "border-error/40 bg-error/10 text-error",
      pulse: false,
    },
    [DeviceOperationalStatus.MAINTENANCE]: {
      label: "Maintenance",
      className: "border-primary/15 bg-primary/10 text-primary",
      pulse: false,
    },
    [DeviceOperationalStatus.VISUALIZATION_DISABLED]: {
      label: "Visualization disabled",
      className: "border-warning/40 bg-warning/10 text-warning",
      pulse: false,
    },
    [DeviceOperationalStatus.INGESTION_DISABLED]: {
      label: "Ingestion disabled",
      className: "border-error/40 bg-error/10 text-error",
      pulse: false,
    },
  }[status];

  return (
    <div
      className={`inline-flex items-center gap-ds-2 rounded-full border px-ds-3 py-ds-1 text-micro font-semibold uppercase tracking-widest ${cfg.className}`}
    >
      <span className="relative flex h-2 w-2">
        {cfg.pulse ? (
          <>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-40" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
          </>
        ) : (
          <span className="relative inline-flex h-2 w-2 rounded-full bg-current opacity-70" />
        )}
      </span>
      {formatDeviceOperationalStatusLabel(status)}
    </div>
  );
}

function OverviewStat({
  icon: Icon,
  title,
  value,
  hint,
}: {
  icon: LucideIcon;
  title: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-control border border-card-border bg-surface-primary p-ds-4">
      <div className="flex items-start gap-ds-3">
        <div className="rounded-control border border-card-border bg-surface-secondary p-ds-2 text-primary">
          <Icon className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">{title}</p>
          <p className="mt-ds-1 text-body font-semibold text-foreground">{value}</p>
          <p className="mt-1 text-micro text-muted">{hint}</p>
        </div>
      </div>
    </div>
  );
}

export function DeviceDetailView({ device }: DeviceDetailViewProps) {
  const dialogs = useAppDialogs();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const opModel = deviceActivityModelFromApi(device);

  // ── Connection status via standalone hook ────────────────────────
  const { connections, loading: connLoading, refresh: refreshConnections } =
    useDataloggerConnections(device.id);
  // ────────────────────────────────────────────────────────────────

  const meta = asJsonObject(device.metadata);
  const basicMeta = asJsonObject(meta.basic);
  const hardwareMeta = asJsonObject(meta.hardware);
  const channels = useMemo(
    () => getChannelMetadata(meta, device.channels),
    [device.channels, meta],
  );
  const lastSeen = formatDeviceLastSeen(device.last_seen);
  const protocol = getDeviceProtocolLabel(device);
  const sampling = getDeviceSamplingLabel(device);
  const location = getDeviceLocationLabel(device);
  const lifecycle = getDeviceLifecycleDates(device);
  const description =
    readMetaString(basicMeta, "description") ||
    readMetaString(meta, "description") ||
    "--";
  const devicePhotoPath = readMetaString(meta, "device_photo_path");
  const transmission =
    readMetaString(hardwareMeta, "transmissionMode") ||
    readMetaString(meta, "transmission_mode") ||
    "--";
  const normalizedTransmission = transmission.trim().toLowerCase();
  const isMqttProtocol =
    normalizedTransmission === "mqtt" ||
    connections.some((connection) => connection.connectionType.toLowerCase() === "mqtt");
  const hasBluetoothProtocol =
    normalizedTransmission === "bluetooth_serial" ||
    normalizedTransmission === "bluetooth" ||
    connections.some((connection) => connection.connectionType.toLowerCase() === "bluetooth");
  const visibleConnections = hasBluetoothProtocol
    ? connections.filter((connection) => connection.connectionType.toLowerCase() !== "bluetooth")
    : connections;
  const showConnectionStatus = !hasBluetoothProtocol || visibleConnections.length > 0;
  const firmware = device.firmware_version?.trim() || "--";
  const publicQrUrl = device.qr_public_token
    ? `${window.location.origin}/public/devices/${device.qr_public_token}`
    : null;
  const provisioningMeta = asJsonObject(meta.provisioning);
  const provisioningValue = readMetaString(provisioningMeta, "api_key");
  const mqttTopic = useMemo(
    () => (isMqttProtocol ? resolveDeviceMqttTopic(meta, hardwareMeta, device.device_uid) : null),
    [device.device_uid, hardwareMeta, isMqttProtocol, meta],
  );
  const telemetryPayload = useMemo(() => {
    const data = Object.fromEntries(
      (device.channels ?? [])
        .filter((channel) => channel.key?.trim() && channel.data_type === "number")
        .map((channel, index) => [channel.key, buildSampleChannelValue(channel, index)]),
    );

    return JSON.stringify(
      {
        device_id: device.id,
        api_keys: provisioningValue || "your-api-key",
        time: new Date().toISOString(),
        data,
      },
      null,
      2,
    );
  }, [device.channels, device.id, provisioningValue]);

  const handleCopy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      await dialogs.copy({
        title: `Copy ${label}`,
        description: "Clipboard access is unavailable. Copy the value manually below.",
        label,
        value,
      });
    }
    setCopiedField(label);
    window.setTimeout(() => {
      setCopiedField((current) => (current === label ? null : current));
    }, 1200);
  };

  return (
    <div className="space-y-ds-8">
      <PageHeader
        title={device.name?.trim() || device.device_uid}
        topRow={
          <div className="flex flex-col gap-ds-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-ds-2">
              <Link to="/devices" className="btn btn-secondary btn-sm gap-ds-2 !no-underline">
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                Back
              </Link>
              <StatusBadge status={opModel.status} />
              <div className="inline-flex max-w-full flex-wrap items-center gap-ds-2 rounded-[var(--radius-md)] border border-border bg-muted-bg/55 px-ds-3 py-ds-2 font-sans text-micro text-muted">
                <span className="font-semibold text-foreground">Last signal</span>
                <span>{lastSeen}</span>
                <span className="text-border-strong/70">/</span>
                <span className="font-semibold text-foreground">Firmware</span>
                <span>{firmware}</span>
                <span className="text-border-strong/70">/</span>
                <span>{opModel.delayLabel}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-ds-2 sm:justify-end">
              <GuardedActionLink
                to={`/devices/${device.id}/edit`}
                moduleKey="devices"
                action="edit"
                deniedMessage="You don't have permission to edit this data logger."
                className="btn btn-primary btn-sm !px-2"
                title="Edit device"
                aria-label="Edit device"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
              </GuardedActionLink>
              <Link to="/live" className="btn btn-secondary btn-sm gap-ds-2 !no-underline">
                <Waves className="h-3.5 w-3.5" aria-hidden />
                Live signals
              </Link>
              <Link to={`/explorer?device=${device.id}`} className="btn btn-secondary btn-sm gap-ds-2 !no-underline">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                Explorer
              </Link>
            </div>
          </div>
        }
      />

      {/* ── Overview ─────────────────────────────────────────────── */}
      {publicQrUrl ? (
        <Card className="flex flex-col gap-ds-4 border-card-border p-ds-5 shadow-card sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-ds-4">
            {device.qr_public_enabled ? <img src={`${API_BASE}/devices/public/${device.qr_public_token}/qr.svg`} alt="Public device QR code" className="h-20 w-20 rounded-control border border-card-border bg-white p-1" /> : <span className="grid h-20 w-20 place-items-center rounded-control bg-muted-bg text-muted"><QrCode className="h-8 w-8" /></span>}
            <div className="min-w-0"><p className="text-caption font-semibold text-foreground">Public QR page</p><p className="mt-1 truncate text-micro text-muted">{device.qr_public_enabled ? publicQrUrl : "Disabled — enable it in Edit → QR / public."}</p></div>
          </div>
          {device.qr_public_enabled ? <div className="flex shrink-0 gap-ds-2"><button type="button" className="btn btn-secondary btn-sm" onClick={() => void handleCopy("public_qr_url", publicQrUrl)}><Copy className="h-3.5 w-3.5" />{copiedField === "public_qr_url" ? "Copied" : "Copy link"}</button><a className="btn btn-secondary btn-sm !no-underline" href={publicQrUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5" />Open</a></div> : null}
        </Card>
      ) : null}

      <PageSection
        title="Overview"
        description="Read-only device summary. Channels, data settings, connectivity, and lifecycle are grouped here for quick review."
      >
        <div className="grid gap-ds-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.9fr)]">
          <Card className="space-y-ds-5 border-border/50 p-ds-5 shadow-card">
            <div>
              <p className="text-micro font-semibold uppercase tracking-[0.14em] text-muted">
                Identity
              </p>
              <h3 className="mt-ds-1 text-title font-semibold text-foreground">
                {device.name?.trim() || device.device_uid}
              </h3>
              <p className="mt-ds-2 text-caption text-muted">{description}</p>
            </div>

            <div className="grid gap-ds-3">
              <div className="rounded-control border border-card-border bg-surface-primary p-ds-3">
                <div className="flex items-start justify-between gap-ds-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">
                      Example Ingest Payload
                    </p>
                    <p className="mt-1 text-micro text-muted">
                      {mqttTopic
                        ? "Use the MQTT topic below for broker publish, or send this same payload to `/telemetry/sync` over HTTP."
                        : "Ready-to-copy `/telemetry/sync` body with this device ID, API key, and channel keys."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleCopy("telemetry_payload", telemetryPayload)}
                    className="inline-flex items-center gap-1 rounded-lg border border-border/50 bg-muted-bg/30 px-ds-2 py-ds-1 text-micro text-muted transition hover:bg-muted-bg/50 hover:text-foreground"
                    aria-label="Copy telemetry payload"
                  >
                    {copiedField === "telemetry_payload" ? (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    {copiedField === "telemetry_payload" ? "Copied" : "Copy"}
                  </button>
                </div>
                {mqttTopic ? (
                  <div className="mt-ds-3 rounded-control border border-primary/15 bg-primary/5 p-ds-3">
                    <div className="flex items-start justify-between gap-ds-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">
                          MQTT Publish Topic
                        </p>
                        <p className="mt-1 break-all font-mono text-caption text-foreground">
                          {mqttTopic}
                        </p>
                        <p className="mt-2 text-micro text-muted">
                          Publish to this exact topic for MQTT. Use device UID in the topic path, not device ID.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleCopy("mqtt_topic", mqttTopic)}
                        className="inline-flex items-center gap-1 rounded-lg border border-border/50 bg-card px-ds-2 py-ds-1 text-micro text-muted transition hover:bg-muted-bg/50 hover:text-foreground"
                        aria-label="Copy MQTT topic"
                      >
                        {copiedField === "mqtt_topic" ? (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                        {copiedField === "mqtt_topic" ? "Copied" : "Copy topic"}
                      </button>
                    </div>
                  </div>
                ) : null}
                <pre className="mt-ds-3 overflow-x-auto rounded-control border border-card-border bg-surface-inset p-ds-3 text-micro leading-6 text-foreground">
                  <code>{telemetryPayload}</code>
                </pre>
              </div>
            </div>

            <div className="grid gap-ds-3 sm:grid-cols-2">
              <OverviewStat
                icon={MapPinned}
                title="Location"
                value={location.primary}
                hint={location.secondary || "No city/state"}
              />
              <OverviewStat
                icon={Cable}
                title="Protocol"
                value={protocol}
                hint={`Mode: ${transmission}`}
              />
              <OverviewStat
                icon={Gauge}
                title="Sampling"
                value={sampling.primary}
                hint={sampling.secondary}
              />
              <OverviewStat
                icon={Layers3}
                title="Channels"
                value={String(channels.length)}
                hint="Declared acquisition channels"
              />
            </div>

            <div className="rounded-control border border-card-border bg-surface-inset p-ds-4">
              <div className="mb-ds-3 flex items-center gap-ds-2">
                <Wrench className="h-4 w-4 text-primary" aria-hidden />
                <h4 className="text-body font-semibold text-foreground">Lifecycle</h4>
              </div>
              <div className="grid gap-ds-3 sm:grid-cols-3">
                {lifecycle.length > 0 ? (
                  lifecycle.map((item) => (
                    <div
                      key={item.label}
                      className="rounded-control border border-card-border bg-surface-primary p-ds-3"
                    >
                      <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">
                        {item.label}
                      </p>
                      <p className="mt-1 text-caption font-medium text-foreground">{item.value}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-caption text-muted sm:col-span-3">
                    No lifecycle dates configured.
                  </div>
                )}
              </div>
            </div>
          </Card>

          <div className="space-y-ds-4">
            {devicePhotoPath ? (
              <Card className="border-border/50 p-ds-5 shadow-card">
                <div className="mb-ds-3 flex items-center gap-ds-2">
                  <MapPinned className="h-4 w-4 text-primary" aria-hidden />
                  <h4 className="text-body font-semibold text-foreground">Device image</h4>
                </div>
                <div className="flex min-h-[16rem] items-center justify-center rounded-control border border-card-border bg-surface-inset p-ds-3">
                  <PrivateDeviceImage
                    photoPath={devicePhotoPath}
                    alt={device.name?.trim() || device.device_uid}
                    className="max-h-[20rem] w-full rounded-control object-contain"
                  />
                </div>
              </Card>
            ) : null}

            <Card className="border-border/50 p-ds-5 shadow-card">
              <div className="mb-ds-3 flex items-center gap-ds-2">
                <Layers3 className="h-4 w-4 text-primary" aria-hidden />
                <h4 className="text-body font-semibold text-foreground">Channels</h4>
              </div>
              {channels.length > 0 ? (
                <div className="space-y-ds-2">
                  {channels.map((channel) => (
                    <div
                      key={channel.id}
                      className="rounded-control border border-card-border bg-surface-inset px-ds-3 py-ds-3"
                    >
                      <div className="flex items-center justify-between gap-ds-2">
                        <p className="truncate text-caption font-semibold text-foreground">
                          {channel.label}
                        </p>
                        <span className="truncate text-micro text-muted">
                          {channel.unit || "--"}
                        </span>
                      </div>
                      <div className="mt-2 grid gap-ds-2 sm:grid-cols-3">
                        <div className="rounded-lg border border-border/30 bg-card/80 px-ds-2 py-ds-2">
                          <p className="text-[0.6rem] uppercase tracking-wider text-muted">Prefix</p>
                          <p className="mt-1 text-micro text-foreground">{channel.prefix || "--"}</p>
                        </div>
                        <div className="rounded-lg border border-border/30 bg-card/80 px-ds-2 py-ds-2">
                          <p className="text-[0.6rem] uppercase tracking-wider text-muted">Suffix</p>
                          <p className="mt-1 text-micro text-foreground">{channel.suffix || "--"}</p>
                        </div>
                        <div className="rounded-lg border border-border/30 bg-card/80 px-ds-2 py-ds-2">
                          <p className="text-[0.6rem] uppercase tracking-wider text-muted">
                            Thresholds
                          </p>
                          <p className="mt-1 text-micro text-foreground">
                            {channel.thresholdLow != null || channel.thresholdHigh != null
                              ? `${channel.thresholdLow != null ? `Low ${channel.thresholdLow}` : "Low --"} | ${channel.thresholdHigh != null ? `High ${channel.thresholdHigh}` : "High --"}`
                              : "--"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-caption text-muted">No channels declared for this device.</p>
              )}
            </Card>
          </div>
        </div>
      </PageSection>

      {/* ── Connectivity ─────────────────────────────────────────── */}
      <PageSection
        title="Connectivity"
        description="Live heartbeat monitoring, connection health per protocol, and offline data recovery for all configured links."
      >
        {showConnectionStatus ? (
          <DataloggerConnectionStatus
            deviceId={device.id}
            deviceUid={device.device_uid}
            connections={visibleConnections}
            isLoading={connLoading}
            onRefresh={refreshConnections}
            onTriggerBatchPush={async (_connId) => {
              await fetch(`${API_BASE}/datalogger/batch`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  device_uid: device.device_uid,
                  readings: [],
                }),
              });
              await refreshConnections();
            }}
          />
        ) : null}
        {hasBluetoothProtocol ? (
          <div className={showConnectionStatus ? "mt-ds-4 space-y-ds-4" : "space-y-ds-4"}>
            <BluetoothWebSerialBridge deviceId={device.id} apiKey={provisioningValue || null} />
          </div>
        ) : null}
      </PageSection>

     
    </div>
  );
}
